import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardLayout, Card, Grid, CardIcon, CardTitle, CardValue, CardDescription, ActionButton } from '../../components/DashboardLayout';
import glassTheme from '../../styles/glassTheme';
import { dashboardApi, ModeratorDashboardData } from '../../services/dashboardApi';

const StatCard = styled(Card)`
  background: linear-gradient(135deg, rgba(255, 184, 0, 0.1) 0%, rgba(255, 215, 0, 0.1) 100%);
  border: 1px solid rgba(255, 184, 0, 0.3);
`;

const ContentItem = styled.div`
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all ${glassTheme.transition.base};

  &:hover {
    background: rgba(0, 0, 0, 0.3);
    transform: translateX(5px);
  }
`;

const ContentIcon = styled.div<{ type: 'pending' | 'approved' | 'rejected' }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => {
    switch (props.type) {
      case 'pending': return 'rgba(255, 184, 0, 0.2)';
      case 'approved': return 'rgba(0, 255, 0, 0.2)';
      case 'rejected': return 'rgba(255, 0, 0, 0.2)';
      default: return 'rgba(128, 128, 128, 0.2)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const ContentDetails = styled.div`
  flex: 1;
`;

const ContentTitle = styled.div`
  color: ${glassTheme.colors.text.primary};
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const ContentMeta = styled.div`
  color: ${glassTheme.colors.text.muted};
  font-size: 0.85rem;
`;

const StatusBadge = styled.span<{ status: 'pending' | 'approved' | 'rejected' }>`
  padding: 0.25rem 0.75rem;
  border-radius: ${glassTheme.borderRadius.md};
  font-size: 0.8rem;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'pending': return 'rgba(255, 184, 0, 0.2)';
      case 'approved': return 'rgba(0, 255, 0, 0.2)';
      case 'rejected': return 'rgba(255, 0, 0, 0.2)';
      default: return 'rgba(128, 128, 128, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending': return '#FFB800';
      case 'approved': return '#00FF00';
      case 'rejected': return '#FF0000';
      default: return '#888888';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ApproveButton = styled(ActionButton)`
  background: rgba(0, 255, 0, 0.2);
  border: 1px solid #00FF00;
  color: #00FF00;
  padding: 0.4rem 0.8rem;

  &:hover {
    background: rgba(0, 255, 0, 0.3);
  }
`;

const RejectButton = styled(ActionButton)`
  background: rgba(255, 0, 0, 0.2);
  border: 1px solid #FF0000;
  color: #FF0000;
  padding: 0.4rem 0.8rem;

  &:hover {
    background: rgba(255, 0, 0, 0.3);
  }
`;

export const ModeratorDashboard: React.FC = () => {
  const [data, setData] = useState<ModeratorDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setData(await dashboardApi.getModerator());
      } catch (err) {
        console.error('Failed to load moderator dashboard:', err);
        setError('无法加载审核数据，请稍后重试');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = data?.stats;

  const relativeTime = (iso: string) => {
    if (!iso) return '';
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 60) return `${Math.max(mins, 1)} 分钟前`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} 小时前`;
    return `${Math.floor(hours / 24)} 天前`;
  };

  const understandingLabel = (u: string) =>
    u === 'high' ? '高' : u === 'medium' ? '中' : u === 'low' ? '低' : u;

  return (
    <DashboardLayout
      title="内容审核员仪表盘"
      subtitle="审核 AI 反馈质量与学生自我解释"
    >
      {/* This platform has no peer-visible content, so there is no user-content
          moderation queue. This view reviews AI feedback quality instead. */}
      {data?.note && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <CardIcon>ℹ️</CardIcon>
          <CardDescription>{data.note}</CardDescription>
        </Card>
      )}
      {error && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <CardIcon>⚠️</CardIcon>
          <CardDescription>{error}</CardDescription>
        </Card>
      )}

      <Grid>
        <StatCard>
          <CardIcon>📝</CardIcon>
          <CardValue>{stats ? stats.totalExplanations : '—'}</CardValue>
          <CardTitle>自我解释总数</CardTitle>
          <CardDescription>学生提交的解释数量</CardDescription>
        </StatCard>

        <StatCard>
          <CardIcon>🤖</CardIcon>
          <CardValue>{stats ? stats.withAiFeedback : '—'}</CardValue>
          <CardTitle>已有 AI 反馈</CardTitle>
          <CardDescription>已生成反馈的解释</CardDescription>
        </StatCard>

        <StatCard>
          <CardIcon>⚠️</CardIcon>
          <CardValue>{stats ? stats.missingAiFeedback : '—'}</CardValue>
          <CardTitle>缺少反馈</CardTitle>
          <CardDescription>尚未生成 AI 反馈</CardDescription>
        </StatCard>

        <StatCard>
          <CardIcon>🚩</CardIcon>
          <CardValue>{stats ? stats.flaggedShortHighConfidence : '—'}</CardValue>
          <CardTitle>需核查</CardTitle>
          <CardDescription>自评高但内容极短</CardDescription>
        </StatCard>
      </Grid>

      <Card style={{ marginBottom: '2rem' }}>
        <CardTitle>自评理解程度 vs 实际书写量</CardTitle>
        <CardDescription style={{ marginBottom: '1rem' }}>
          自评为主观指标；若自评越高而书写量越少，说明该指标不宜作为学习成效依据。
        </CardDescription>
        {loading ? (
          <CardDescription>加载中...</CardDescription>
        ) : (data?.byUnderstanding || []).map((b) => (
          <div key={b.understanding} style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
              <span>自评{understandingLabel(b.understanding)}（{b.count} 条）</span>
              <span>平均 {b.avgTokenCount} 词 / {b.avgCharCount} 字</span>
            </div>
            <div style={{ width: '100%', height: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px' }}>
              <div style={{ width: `${Math.min(b.avgCharCount / 2, 100)}%`, height: '100%', background: '#00B8FF', borderRadius: '4px' }}></div>
            </div>
          </div>
        ))}
      </Card>

      <Card style={{ marginBottom: '2rem' }}>
        <CardTitle>需核查的解释</CardTitle>
        <CardDescription style={{ marginBottom: '1rem' }}>
          学生自评理解程度为“高”，但书写量极少
        </CardDescription>
        {loading ? (
          <CardDescription>加载中...</CardDescription>
        ) : (data?.flagged || []).length === 0 ? (
          <CardDescription>暂无需核查的内容</CardDescription>
        ) : (data?.flagged || []).map((f, i) => (
          <ContentItem key={i}>
            <ContentIcon type="pending">💭</ContentIcon>
            <ContentDetails>
              <ContentTitle>{f.topicTitle || '未知主题'}</ContentTitle>
              <ContentMeta>
                {f.tokenCount} 词 · {f.timeSpent}s · {relativeTime(f.createdAt)}
              </ContentMeta>
            </ContentDetails>
            <StatusBadge status="pending">自评{understandingLabel(f.understanding)}</StatusBadge>
          </ContentItem>
        ))}
      </Card>

      <Card>
        <CardTitle>待处理：缺少反馈或内容过短</CardTitle>
        {loading ? (
          <CardDescription>加载中...</CardDescription>
        ) : (data?.needsReview || []).length === 0 ? (
          <CardDescription>暂无待处理内容</CardDescription>
        ) : (data?.needsReview || []).map((n, i) => (
          <ContentItem key={i}>
            <ContentIcon type="pending">📝</ContentIcon>
            <ContentDetails>
              <ContentTitle>{n.topicTitle || '未知主题'}</ContentTitle>
              <ContentMeta>{n.tokenCount} 词 · {relativeTime(n.createdAt)}</ContentMeta>
            </ContentDetails>
            <StatusBadge status="pending">待处理</StatusBadge>
          </ContentItem>
        ))}
      </Card>
    </DashboardLayout>
  );
};

